import { useState, useEffect } from 'react';
import { useTr } from '../utils/useTr';
import { useApp } from '../contexts/AppContext';
import { BadgePill, LocationIcon, WorkIcon } from './icons';
import { getPublicBadges } from '../utils/badges';
import { ReportButton } from './ReportBlock';
import FriendRadar, { FriendRadarLegend } from './FriendRadar';
import { SpotifyBadge } from './SpotifyConnect';
import {
  computeRadarCompat, computeWeightedScore,
  buildAxisNarrative, buildPortraitCroisé, AXIS_META,
} from '../utils/matching';

// ── Shared helpers ────────────────────────────────────────────────────────────
function mapFullProfile(raw, base) {
  return {
    ...(base || {}),
    ...raw,
    photos: (raw.photos?.length > 0) ? raw.photos : (base?.photos ?? []),
    educationField:   raw.education_field   ?? base?.educationField,
    relationshipGoal: raw.relationship_goal ?? base?.relationshipGoal,
    loveLanguage:     raw.love_language      ?? base?.loveLanguage,
    sleepSchedule:    raw.sleep              ?? base?.sleepSchedule,
    school:           raw.school,
    religion:         raw.religion,
    distance:         base?.distance ?? base?.distance_km,
    prompt1: raw.prompt1_q ? { q: raw.prompt1_q, a: raw.prompt1_a } : (base?.prompt1 ?? null),
    prompt2: raw.prompt2_q ? { q: raw.prompt2_q, a: raw.prompt2_a } : (base?.prompt2 ?? null),
    prompt3: raw.prompt3_q ? { q: raw.prompt3_q, a: raw.prompt3_a } : (base?.prompt3 ?? null),
    friendQuotes:        raw.friend_ratings?.quotes ?? base?.friendQuotes ?? [],
    criteria_scores:     raw.criteria_scores ?? base?.criteria_scores ??
      (base?.friendRatings || base?.criteriaScores
        ? { ...(base?.friendRatings || {}), ...(base?.criteriaScores || {}) }
        : {}),
    criteria_scores_fit: raw.criteria_scores_fit ?? base?.criteria_scores_fit ?? base?.needScores ?? {},
  };
}

function LifeCard({ label, val }) {
  return (
    <div style={{ padding:'0.45rem 0.6rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)' }}>
      <div style={{ fontSize:'0.52rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.15rem' }}>{label}</div>
      <div style={{ fontSize:'0.78rem', color:'var(--white)' }}>{val}</div>
    </div>
  );
}

// ── Feature I — Friend Overlap section ──────────────────────────────────────
function FriendOverlapSection({ viewerId, targetId }) {
  const { features, lang } = useApp();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!features?.feature_friend_overlap) return;
    if (!viewerId || !targetId || viewerId === targetId) return;
    let alive = true;
    import('../utils/featurePackA').then(({ fetchFriendOverlap }) => {
      fetchFriendOverlap(viewerId, targetId).then(d => { if (alive) setRows(d || []); });
    });
    return () => { alive = false; };
  }, [features?.feature_friend_overlap, viewerId, targetId]);

  if (!features?.feature_friend_overlap || rows.length === 0) return null;
  const fr = lang === 'fr';
  return (
    <div style={{
      marginTop: '0.9rem', padding: '10px 12px',
      background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.25)',
      borderRadius: 8,
    }}>
      <p style={{ margin: 0, fontSize: '0.6rem', letterSpacing: '0.14em',
        textTransform: 'uppercase', color: 'var(--gold)' }}>
        {fr ? 'En commun' : 'In common'}
      </p>
      <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--white)', lineHeight: 1.5 }}>
        {fr
          ? `${rows.length} ami${rows.length > 1 ? 's' : ''} vous a${rows.length > 1 ? 'ont' : ''} noté${rows.length > 1 ? 's' : ''} tou${rows.length > 1 ? 's' : ''} les deux : `
          : `${rows.length} friend${rows.length > 1 ? 's' : ''} rated both of you: `}
        <strong style={{ color: 'var(--gold)' }}>
          {rows.map(r => r.rater_name).filter(Boolean).join(' · ') || (fr ? 'amis communs' : 'shared friends')}
        </strong>
      </p>
    </div>
  );
}

export function ProfileSections({ dUser, currentUser }) {
  const tr = useTr();
  const { lang } = useApp();

  const SectionHeader = ({ label }) => (
    <p style={{ fontSize:'0.58rem', letterSpacing:'0.18em', textTransform:'uppercase',
      color:'var(--gold)', marginBottom:'0.55rem', marginTop:'1.2rem',
      display:'flex', alignItems:'center', gap:'0.5rem' }}>
      {label}
      <span style={{ flex:1, height:'1px', background:'var(--border)', display:'inline-block' }}/>
    </p>
  );

  const goal  = {relationship:tr('val.relationship'),casual:tr('val.casual'),friendship:tr('val.friendship'),marriage:tr('val.marriage'),unsure:tr('discover.not_sure')};
  const edu   = {bachelors:tr('val.bachelors'),masters:tr('val.masters'),phd:tr('val.phd'),highschool:tr('profile.edu_highschool'),'some-college':tr('profile.edu_college'),trade:tr('val.trade')};
  const drink = {never:tr('val.never'),rarely:tr('val.rarely'),socially:tr('val.socially'),regularly:tr('val.regularly'),daily:tr('val.daily')};
  const smoke  = {never:tr('val.never'),rarely:tr('val.rarely'),socially:tr('val.socially'),daily:tr('val.daily')};
  const exer   = {daily:tr('val.daily')||'Every day','3-4x/week':'3–4×/week','1-2x/week':'1–2×/week',rarely:tr('val.rarely')||'Rarely',never:tr('val.never')||'Never'};
  const sleepMap = {early:tr('profile.sleep_early')||'Early bird',night:tr('profile.sleep_night')||'Night owl',flexible:tr('profile.sleep_flexible')||'Flexible'};
  const petMap = {cat:tr('val.cat'),dog:tr('val.dog'),both:tr('val.both_pets'),none:tr('val.no_pets'),allergic:tr('val.allergic'),other:tr('profile.edu_other2')};
  const ll     = {words:tr('val.words'),acts:tr('val.acts'),gifts:tr('val.gifts'),quality:tr('val.quality'),touch:tr('val.touch')};

  return (
    <div style={{ paddingBottom:'0.5rem' }}>
      {dUser.bio && (
        <p style={{ fontSize:'0.9rem', color:'var(--white)', lineHeight:1.75, marginTop:'0.2rem' }}>{dUser.bio}</p>
      )}
      {dUser.mbti && (
        <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap', marginTop:'0.8rem' }}>
          <span className="chip">{dUser.mbti}</span>
        </div>
      )}
      {(dUser.height || dUser.education || dUser.educationField || dUser.school || (dUser.languages||[]).length > 0 || dUser.relationshipGoal || dUser.zodiac) && <>
        <SectionHeader label={tr('section.basics') || 'Basics'}/>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.4rem' }}>
          {dUser.height          && <LifeCard label={tr('about.height_label')||'Height'}      val={`${dUser.height} cm`}/>}
          {dUser.zodiac          && <LifeCard label={tr('about.zodiac')||'Zodiac'}             val={dUser.zodiac}/>}
          {dUser.education       && <LifeCard label={tr('profile.edu_label')||'Education'}     val={edu[dUser.education] || dUser.education}/>}
          {dUser.educationField  && <LifeCard label={tr('about.field_study')||'Field'}         val={dUser.educationField}/>}
          {dUser.school          && <LifeCard label={tr('about.school')||'School'}             val={dUser.school}/>}
          {(dUser.languages||[]).length > 0 && <LifeCard label={tr('about.languages')||'Languages'} val={dUser.languages.join(', ')}/>}
          {dUser.relationshipGoal && <LifeCard label={tr('discover.looking_for_label')||'Looking for'} val={goal[dUser.relationshipGoal] || dUser.relationshipGoal}/>}
        </div>
      </>}
      {[dUser.prompt1, dUser.prompt2, dUser.prompt3].filter(p=>p?.a).length > 0 && <>
        <SectionHeader label={tr('section.prompts')}/>
        {[dUser.prompt1, dUser.prompt2, dUser.prompt3].filter(p=>p?.a).map((p,i) => (
          <div key={i} style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'0.85rem 1rem', marginBottom:'0.5rem' }}>
            <p style={{ fontSize:'0.58rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--gold)', marginBottom:'0.3rem' }}>{p.q}</p>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.05rem', color:'var(--white)', lineHeight:1.6 }}>{p.a}</p>
          </div>
        ))}
      </>}
      {(dUser.interests||[]).length > 0 && <>
        <SectionHeader label={tr('section.interests')}/>
        <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
          {(dUser.interests||[]).map((t,i)=><span key={i} className="chip">{tr(t)||t}</span>)}
        </div>
      </>}
      {(dUser.drinking || dUser.smoking || dUser.exercise || dUser.diet || dUser.pets || dUser.loveLanguage || dUser.sleepSchedule || dUser.sleep || dUser.cannabis) && <>
        <SectionHeader label={tr('section.lifestyle')}/>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.4rem' }}>
          {dUser.drinking     && <LifeCard label={tr('life.drinking')}   val={drink[dUser.drinking]||dUser.drinking}/>}
          {dUser.smoking      && <LifeCard label={tr('life.smoking')}    val={smoke[dUser.smoking]||dUser.smoking}/>}
          {dUser.cannabis     && <LifeCard label={tr('life.cannabis')}   val={smoke[dUser.cannabis]||dUser.cannabis}/>}
          {dUser.exercise     && <LifeCard label={tr('life.exercise')}   val={exer[dUser.exercise]||dUser.exercise}/>}
          {dUser.diet         && <LifeCard label={tr('life.diet')}       val={tr('profile.diet_'+dUser.diet)||dUser.diet}/>}
          {dUser.pets         && <LifeCard label={tr('life.pets')}       val={petMap[dUser.pets]||dUser.pets}/>}
          {dUser.loveLanguage && <LifeCard label={tr('life.love_lang')}  val={ll[dUser.loveLanguage]||dUser.loveLanguage}/>}
          {(dUser.sleepSchedule||dUser.sleep) && <LifeCard label={tr('life.sleep')} val={sleepMap[dUser.sleepSchedule||dUser.sleep]||(dUser.sleepSchedule||dUser.sleep)}/>}
        </div>
      </>}
      {(dUser.religion || dUser.politics) && <>
        <SectionHeader label={tr('section.values')}/>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.4rem' }}>
          {dUser.religion && <LifeCard label={tr('life.religion')}  val={tr('profile.religion_'+dUser.religion)||tr(dUser.religion)||dUser.religion}/>}
          {dUser.politics && <LifeCard label={tr('life.politics')}  val={tr('profile.politics_'+dUser.politics)||tr(dUser.politics)||dUser.politics}/>}
        </div>
      </>}
      <SpotifyBadge user={dUser}/>
      <FriendOverlapSection viewerId={currentUser?.id} targetId={dUser?.id}/>
      {(dUser.friendQuotes||[]).length > 0 && <>
        <SectionHeader label={tr('section.from_friends')}/>
        {dUser.friendQuotes.map((q,i) => {
          const text   = typeof q === 'string' ? q : q.text;
          const author = typeof q === 'object' ? q.author : null;
          const rating = typeof q === 'object' ? q.rating : null;
          return (
            <div key={i} style={{ borderLeft:'2px solid var(--gold-border)', paddingLeft:'0.8rem', marginBottom:'0.75rem' }}>
              <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', fontSize:'0.95rem', color:'var(--muted)', lineHeight:1.65, margin:'0 0 0.25rem' }}>"{text}"</p>
              {(author||rating) && (
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                  {author && <span style={{ fontSize:'0.65rem', color:'var(--muted)', opacity:0.6 }}>— {author}</span>}
                  {rating && <span style={{ fontSize:'0.65rem', fontWeight:600, color:'var(--gold)', background:'rgba(201,168,76,0.1)', border:'1px solid rgba(201,168,76,0.2)', borderRadius:99, padding:'1px 7px' }}>★ {rating.toFixed(1)}</span>}
                </div>
              )}
            </div>
          );
        })}
      </>}
    </div>
  );
}

// ── Main sheet component ──────────────────────────────────────────────────────
// mode='discover' → shows pass + like/write buttons
// mode='matches'  → shows close + write buttons
export default function ProfileSheet({ user: baseProp, onClose, mode = 'discover', onPass, onLike, onWrite, likes, preview = false }) {
  const tr = useTr();
  const { currentUser, lang, getCompatibility } = useApp();

  const [photoIdx,    setPhotoIdx]    = useState(0);
  const [detailTab,   setDetailTab]   = useState('profile');
  const [radarTab,    setRadarTab]    = useState('radar1');
  const [fullProfile, setFullProfile] = useState(null);
  const [scoresFit,   setScoresFit]   = useState(null);

  // Reset state on new profile
  useEffect(() => {
    setPhotoIdx(0);
    setDetailTab('profile');
    setRadarTab('radar1');
    setFullProfile(null);
    setScoresFit(null);
    if (!baseProp?.id) return;
    import('../utils/supabase').then(({ supabase }) => {
      supabase?.from('user_scores').select('criteria_scores_fit').eq('user_id', baseProp.id).single()
        .then(({ data }) => { if (data) setScoresFit(data.criteria_scores_fit || {}); });
      supabase?.from('users')
        .select('id,bio,height,education,education_field,school,languages,city,zodiac,mbti,relationship_goal,love_language,drinking,smoking,cannabis,exercise,diet,pets,sleep,religion,prompt1_q,prompt1_a,prompt2_q,prompt2_a,prompt3_q,prompt3_a,interests,criteria_scores,friend_ratings,verified')
        .eq('id', baseProp.id).single()
        .then(({ data }) => { if (data) setFullProfile(data); });
    });
  }, [baseProp?.id]);

  if (!baseProp) return null;

  const dUser = (fullProfile && fullProfile.id === baseProp.id)
    ? mapFullProfile(fullProfile, baseProp)
    : baseProp;

  const photos = (dUser.photos || []).filter(Boolean);
  const photoCount = photos.length || 1;
  const photoSrc = photos[photoIdx] || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&q=80';
  const theirName = (dUser.name || 'Eux').split(' ')[0];
  const myName    = (currentUser?.name || 'Toi').split(' ')[0];

  const hasCompatData = Object.keys(currentUser?.criteria_scores || {}).length > 0
                     && Object.keys(dUser.criteria_scores || {}).length > 0;
  const compat = getCompatibility(dUser.id);

  const alignColor = a => a === 'both_high' ? '#4caf50' : a === 'both_low' ? '#ff9800' : a === 'neutral' ? 'var(--muted)' : '#e2785a';
  const alignIcon  = a => a === 'both_high' ? '✦' : a === 'both_low' ? '◈' : a === 'neutral' ? '◌' : '⟷';

  const alreadyLiked = likes?.has(dUser.id);

  return (
    <div className="overlay" onClick={onClose}>
      <div className="sheet" onClick={e => e.stopPropagation()}>

        {/* Pull indicator */}
        <div style={{ textAlign:'center', padding:'0.6rem 0 0' }}>
          <div style={{ width:32, height:2, background:'var(--muted2)', margin:'0 auto', borderRadius:'var(--r-sm)' }}/>
        </div>

        {/* Photo carousel */}
        <div style={{ height:280, position:'relative', overflow:'hidden', margin:'0.8rem 0' }}>
          <img src={photoSrc} alt={dUser.name} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'opacity 0.2s' }}/>
          {photoCount > 1 && <>
            <div onClick={() => setPhotoIdx(i => (i - 1 + photoCount) % photoCount)}
              style={{ position:'absolute', left:0, top:0, width:'35%', height:'100%', cursor:'pointer', zIndex:1 }}/>
            <div onClick={() => setPhotoIdx(i => (i + 1) % photoCount)}
              style={{ position:'absolute', right:0, top:0, width:'35%', height:'100%', cursor:'pointer', zIndex:1 }}/>
            <div style={{ position:'absolute', top:10, left:10, right:10, display:'flex', gap:3, zIndex:2 }}>
              {photos.map((_,i) => (
                <div key={i} onClick={() => setPhotoIdx(i)} style={{ flex:1, height:2, background: i === photoIdx ? 'white' : 'rgba(255,255,255,0.35)', cursor:'pointer', borderRadius:1 }}/>
              ))}
            </div>
            <div style={{ position:'absolute', bottom:10, right:10, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)', borderRadius:99, padding:'2px 8px', fontSize:'0.62rem', color:'rgba(255,255,255,0.85)', fontFamily:'Outfit', zIndex:2 }}>
              {photoIdx + 1}/{photoCount}
            </div>
          </>}
          <button onClick={onClose} style={{ position:'absolute', top:12, right:12, background:'rgba(0,0,0,0.45)', backdropFilter:'blur(4px)', border:'none', color:'#fff', width:30, height:30, borderRadius:'50%', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div style={{ padding:'0 1.2rem' }}>

          {/* Name / info / badges */}
          <div style={{ marginBottom:'0.8rem', marginTop:'0.7rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.4rem' }}>
              <div>
                <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.7rem', fontWeight:500, color:'var(--white)' }}>{dUser.name}, {dUser.age}</h2>
                <p style={{ color:'var(--muted)', fontSize:'0.82rem', marginTop:'0.1rem', display:'flex', alignItems:'center', gap:'0.35rem', flexWrap:'wrap' }}>
                  <WorkIcon size={12} color="var(--muted)"/>{dUser.job}
                  <span style={{opacity:0.4}}>·</span>
                  <LocationIcon size={12} color="var(--muted)"/>{dUser.city}
                  {dUser.distance > 0 && <><span style={{opacity:0.4}}>·</span>{dUser.distance} km</>}
                </p>
              </div>
              {dUser.verified && <span className="badge bdg-green" style={{fontSize:'0.58rem',flexShrink:0}}>✓ Vérifié</span>}
              <ReportButton user={dUser} style={{ marginLeft:'0.3rem' }}/>
            </div>
            {getPublicBadges(dUser, {}, lang).length > 0 && (
              <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap', marginTop:'0.4rem' }}>
                {getPublicBadges(dUser, {}, lang).map(b => <BadgePill key={b.id} badge={b} size="sm"/>)}
              </div>
            )}
          </div>

          {/* Tab bar */}
          {hasCompatData && (
            <div style={{ display:'flex', gap:'0.25rem', marginBottom:'1rem', background:'var(--dark)', borderRadius:'var(--r-md)', padding:'3px', border:'1px solid var(--border)' }}>
              {[{id:'profile',label:'Profil'},{id:'compat',label:'Compatibilité'}].map(tab => (
                <button key={tab.id} onClick={() => setDetailTab(tab.id)}
                  style={{ flex:1, padding:'0.45rem 0', background: detailTab === tab.id ? 'var(--charcoal)' : 'transparent', border: detailTab === tab.id ? '1px solid var(--border)' : '1px solid transparent', borderRadius:'calc(var(--r-md) - 2px)', cursor:'pointer', fontSize:'0.67rem', fontWeight: detailTab === tab.id ? 600 : 400, letterSpacing:'0.06em', textTransform:'uppercase', color: detailTab === tab.id ? 'var(--white)' : 'var(--muted)', transition:'all 0.15s', fontFamily:'Outfit' }}>
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* ── Compat tab ── */}
          {detailTab === 'compat' && hasCompatData && (() => {
            const myScores    = currentUser?.criteria_scores || {};
            const theirScores = dUser.criteria_scores        || {};
            const mySelf      = currentUser?.criteria_scores     || {};
            const myNeed      = currentUser?.criteria_scores_fit || {};
            const theirSelf   = dUser.criteria_scores            || {};
            const theirNeed   = scoresFit || dUser.criteria_scores_fit || {};
            return (
              <>
                {/* Compat score banner */}
                {compat && (() => {
                  const s = compat.score || 0;
                  const s1 = compat.score1 ?? null;
                  const s2 = compat.score2 ?? null;
                  const sc = s >= 80 ? '#C9A84C' : s >= 65 ? '#9CA880' : s >= 50 ? '#7AB8C4' : '#888880';
                  const lbl = s >= 85 ? 'Exceptionnel' : s >= 72 ? 'Très compatible' : s >= 58 ? 'Bon potentiel' : s >= 44 ? 'Quelques différences' : 'Chemins distincts';
                  const hasTwo = s1 != null && s2 != null;
                  return (
                    <div style={{ background:'linear-gradient(135deg,rgba(201,168,76,0.10),rgba(201,168,76,0.04))', border:'1px solid var(--gold-border)', borderRadius:'var(--r-md)', padding:'0.75rem 1rem', display:'flex', alignItems:'center', gap:'1rem', marginBottom:'1rem' }}>
                      <div style={{ textAlign:'center', flexShrink:0 }}>
                        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2.2rem', fontWeight:600, color:sc, lineHeight:1 }}>{s}<span style={{fontSize:'1rem',fontWeight:400}}>%</span></div>
                        <div style={{ fontSize:'0.48rem', color:'var(--muted)', letterSpacing:'0.12em', textTransform:'uppercase', marginTop:1 }}>compatibilité</div>
                      </div>
                      <div style={{ width:1, height:44, background:'var(--gold-border)', flexShrink:0 }}/>
                      <div style={{ flex:1, minWidth:0 }}>
                        {hasTwo ? (<>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.3rem' }}>
                            <span style={{ fontSize:'0.48rem', color:'var(--muted)', whiteSpace:'nowrap', width:60, flexShrink:0 }}>Tu réponds</span>
                            <div style={{ flex:1, height:3, background:'var(--border)', borderRadius:2 }}><div style={{ height:'100%', width:`${s1}%`, background:'#C9A84C', borderRadius:2, transition:'width 0.7s' }}/></div>
                            <span style={{ fontSize:'0.52rem', color:'#C9A84C', fontWeight:600, flexShrink:0, width:28, textAlign:'right' }}>{s1}%</span>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                            <span style={{ fontSize:'0.48rem', color:'var(--muted)', whiteSpace:'nowrap', width:60, flexShrink:0 }}>Eux pour toi</span>
                            <div style={{ flex:1, height:3, background:'var(--border)', borderRadius:2 }}><div style={{ height:'100%', width:`${s2}%`, background:'#E2785A', borderRadius:2, transition:'width 0.7s' }}/></div>
                            <span style={{ fontSize:'0.52rem', color:'#E2785A', fontWeight:600, flexShrink:0, width:28, textAlign:'right' }}>{s2}%</span>
                          </div>
                        </>) : (
                          <div style={{ height:4, background:'var(--border)', borderRadius:2 }}><div style={{ height:'100%', width:`${s}%`, background:sc, borderRadius:2, transition:'width 0.7s' }}/></div>
                        )}
                      </div>
                      <div style={{ fontSize:'0.58rem', color:sc, fontWeight:600, textAlign:'center', flexShrink:0, maxWidth:56, lineHeight:1.3 }}>{lbl}</div>
                    </div>
                  );
                })()}

                {/* Radar */}
                {(() => {
                  const hasMine   = Object.keys(mySelf).length > 0 || Object.keys(myNeed).length > 0;
                  const hasTheirs = Object.keys(theirSelf).length > 0 || Object.keys(theirNeed).length > 0;
                  if (!hasMine && !hasTheirs) return null;
                  const scoreA = radarTab === 'radar1' ? mySelf : myNeed;
                  const scoreB = radarTab === 'radar1' ? theirNeed : theirSelf;
                  const AXES_LIST = ['repair','conflict','accountability','generosity','presence','narrative','stability','boredom','social','stakes'];
                  const FR_LABELS = {repair:'Réparation',conflict:'Conflit',accountability:'Responsabilité',generosity:'Générosité',presence:'Présence',narrative:'Conscience de soi',stability:'Stabilité',boredom:'Aventure',social:'Social',stakes:'Ambition'};
                  const axisDeltas = AXES_LIST.map(ax => ({ key:ax, label:FR_LABELS[ax], a:Number(scoreA?.[ax]||0), b:Number(scoreB?.[ax]||0), delta:Math.abs(Number(scoreA?.[ax]||0)-Number(scoreB?.[ax]||0)) })).sort((x,y)=>y.delta-x.delta);
                  const compatScore  = computeRadarCompat(scoreA, scoreB);
                  const meanGap      = axisDeltas.reduce((s,d)=>s+d.delta,0)/AXES_LIST.length;
                  const tensionCount = axisDeltas.filter(d=>d.delta>=2.5).length;
                  const synergyCount = axisDeltas.filter(d=>d.delta<=1).length;
                  return (
                    <div style={{ marginTop:'1rem' }}>
                      {/* Tab selector */}
                      <div style={{ display:'flex', gap:'0.3rem', marginBottom:'0.75rem' }}>
                        {[{id:'radar1',label:'Toi → '+theirName},{id:'radar2',label:theirName+' → Toi'}].map(t=>(
                          <button key={t.id} onClick={()=>setRadarTab(t.id)} style={{ flex:1, padding:'0.35rem 0.5rem', fontSize:'0.6rem', fontWeight: radarTab===t.id?600:400, background: radarTab===t.id?'var(--dark)':'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', cursor:'pointer', color: radarTab===t.id?'var(--white)':'var(--muted)', fontFamily:'Outfit', letterSpacing:'0.04em', textTransform:'uppercase' }}>{t.label}</button>
                        ))}
                      </div>
                      {/* Stats row */}
                      <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.75rem' }}>
                        {[{label:'Alignement',val:compatScore!=null?`${compatScore}%`:'—'},{label:'Écart moyen',val:meanGap.toFixed(1)},{label:'Synergies',val:synergyCount},{label:'Points de tension',val:tensionCount}].map(s=>(
                          <div key={s.label} style={{ flex:1, textAlign:'center', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', padding:'0.4rem 0.2rem' }}>
                            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', fontWeight:500, color:'var(--white)', lineHeight:1 }}>{s.val}</div>
                            <div style={{ fontSize:'0.44rem', color:'var(--muted)', marginTop:3, opacity:0.7 }}>{s.label}</div>
                          </div>
                        ))}
                      </div>
                      {/* Active radar */}
                      {radarTab === 'radar1' ? (
                        <div style={{ background:'rgba(201,168,76,0.04)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-md)', padding:'0.8rem' }}>
                          <div style={{ display:'flex', justifyContent:'center' }}><FriendRadar selfScores={mySelf} theirNeeds={theirNeed} mode="radar1" size={240}/></div>
                          <FriendRadarLegend mode="radar1" myName="Toi" theirName={theirName}/>
                        </div>
                      ) : (
                        <div style={{ background:'rgba(226,120,90,0.04)', border:'1px solid rgba(226,120,90,0.2)', borderRadius:'var(--r-md)', padding:'0.8rem' }}>
                          <div style={{ display:'flex', justifyContent:'center' }}><FriendRadar needScores={myNeed} theirScores={theirSelf} mode="radar2" size={240}/></div>
                          <FriendRadarLegend mode="radar2" myName="Toi" theirName={theirName}/>
                        </div>
                      )}

                      {/* Narrative card */}
                      {(() => {
                        const { overall, perAxis } = computeWeightedScore(myScores, theirScores);
                        if (!perAxis || Object.keys(perAxis).length === 0) return null;
                        const AXES_ORDER = ['repair','conflict','accountability','generosity','presence','narrative','stability','boredom','social','stakes'];
                        const axEntries = AXES_ORDER.map(ax=>({ax,...perAxis[ax]})).filter(e=>e.a!==undefined);
                        const portrait  = buildPortraitCroisé(myScores, theirScores, myName, theirName, perAxis);
                        return (
                          <>
                            <div style={{ marginTop:'1.2rem', borderTop:'1px solid var(--border)', paddingTop:'1.2rem' }}>
                              <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.05rem', fontWeight:500, color:'var(--white)', marginBottom:'0.5rem', letterSpacing:'0.02em' }}>Lecture de compatibilité</h4>
                              <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', marginBottom:'0.75rem' }}>
                                {[
                                  { color:'#4caf50', icon:'✦', label:'Forte synergie' },
                                  { color:'#ff9800', icon:'◈', label:'Terrain commun à développer' },
                                  { color:'var(--muted)', icon:'◌', label:'Neutre' },
                                  { color:'#e2785a', icon:'⟷', label:'Point de tension' },
                                ].map(({ color, icon, label }) => (
                                  <span key={label} style={{ display:'inline-flex', alignItems:'center', gap:'0.25rem', fontSize:'0.55rem', color:'var(--muted)' }}>
                                    <span style={{ color, fontWeight:700 }}>{icon}</span> {label}
                                  </span>
                                ))}
                              </div>
                              <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                                {axEntries.map(({ax,a,b,alignment})=>{
                                  const phrase = buildAxisNarrative(ax,a,b,myName,theirName);
                                  if (!phrase) return null;
                                  const meta  = AXIS_META[ax];
                                  const color = alignColor(alignment);
                                  const icon  = alignIcon(alignment);
                                  return (
                                    <div key={ax} style={{ display:'flex', gap:'0.6rem', alignItems:'flex-start', background:'rgba(255,255,255,0.02)', border:'1px solid var(--border)', borderLeft:`3px solid ${color}`, borderRadius:'var(--r-sm)', padding:'0.55rem 0.7rem' }}>
                                      <span style={{ fontSize:'0.85rem', flexShrink:0, marginTop:'0.05rem' }}>{meta?.icon||'·'}</span>
                                      <div style={{ flex:1 }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.2rem' }}>
                                          <span style={{ fontSize:'0.6rem', fontWeight:700, color, textTransform:'uppercase', letterSpacing:'0.06em' }}>{icon} {meta?.label}</span>
                                          <span style={{ fontSize:'0.55rem', color:'var(--muted)', opacity:0.5, marginLeft:'auto' }}>{a.toFixed(0)} · {b.toFixed(0)}</span>
                                        </div>
                                        <p style={{ fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.55, margin:0 }}>{phrase}</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            {portrait && (
                              <div style={{ marginTop:'1.2rem', borderTop:'1px solid var(--border)', paddingTop:'1.2rem' }}>
                                <h4 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.05rem', fontWeight:500, color:'var(--white)', marginBottom:'0.85rem', letterSpacing:'0.02em' }}>Portrait croisé</h4>
                                <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                                  {[
                                    {label:'Ce que tu apportes',text:portrait.p1,accent:'var(--gold)'},
                                    {label:`Ce que ${theirName} apporte`,text:portrait.p2,accent:'#4A7FC0'},
                                    {label:'Ce que vous créez ensemble',text:portrait.p3,accent:'#4caf50'},
                                  ].map(({label,text,accent})=>(
                                    <div key={label} style={{ background:'rgba(255,255,255,0.02)', border:`1px solid ${accent}22`, borderLeft:`3px solid ${accent}`, borderRadius:'var(--r-sm)', padding:'0.7rem 0.85rem' }}>
                                      <div style={{ fontSize:'0.6rem', fontWeight:700, color:accent, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'0.35rem' }}>{label}</div>
                                      <p style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', fontSize:'0.9rem', color:'var(--muted)', lineHeight:1.65, margin:0 }}>{text}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  );
                })()}
              </>
            );
          })()}

          {/* ── Profile tab ── */}
          {(detailTab === 'profile' || !hasCompatData) && (
            <ProfileSections dUser={dUser} currentUser={currentUser}/>
          )}

          <div style={{ height:1, background:'var(--border)', margin:'1rem 0' }}/>

          {/* Actions — hidden in preview mode */}
          {!preview && (
            <div style={{ display:'flex', gap:'0.6rem', paddingBottom:'0.5rem' }}>
              {mode === 'discover' && onPass && (
                <button className="btn btn-dark btn-md" style={{ flex:1 }} onClick={onPass}>Passer</button>
              )}
              {mode === 'matches' && (
                <button className="btn btn-dark btn-md" style={{ flex:1 }} onClick={onClose}>Fermer</button>
              )}
              {mode === 'discover' && (
                alreadyLiked
                  ? <button className="btn btn-md" style={{ flex:2.5, background:'rgba(74,127,192,0.12)', border:'1px solid rgba(74,127,192,0.35)', color:'#4A7FC0', fontSize:'0.72rem', letterSpacing:'0.08em', textTransform:'uppercase' }} onClick={onWrite}>Écrire</button>
                  : <button className="btn btn-gold btn-md" style={{ flex:2.5 }} onClick={onLike}>Like ♥</button>
              )}
              {mode === 'matches' && onWrite && (
                <button className="btn btn-gold btn-md" style={{ flex:2.5 }} onClick={onWrite}>Écrire à {theirName} →</button>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
